/**
 * "How you work": timezone, currency, working days and hours. Shared by the
 * business creation wizard and Settings → Business profile. Controlled —
 * the parent owns the value and saves it (PUT /api/businesses/settings, or
 * the create payload).
 */
import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { TimezoneInput, CurrencyPicker } from '../design-system';
import { TimePickerField } from '../TimePickerField';
import { colors, spacing, typography } from '../../theme';
import { withAlpha } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useRTLStyles } from '../../hooks/useRTLStyles';

export const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
export type WeekDay = typeof WEEK_DAYS[number];
export interface WorkingDay { start: string; end: string; isWorking: boolean }
export type WorkingHours = Record<WeekDay, WorkingDay>;

export interface BusinessHoursValue {
  timezone: string;
  currency: string;
  workingHours: WorkingHours;
}

/** Display order starts the week where the region does. */
const DISPLAY_ORDER: WeekDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

interface CountryDefaults { currency: string; timezone: string; workDays: WeekDay[] }
const SUN_THU: WeekDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'];
const MON_FRI: WeekDay[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

// Matched on the stored country name (English or Arabic) — CountryPicker and
// the map picker both store names, not codes.
const COUNTRY_DEFAULTS: Array<{ names: string[]; defaults: CountryDefaults }> = [
  { names: ['egypt', 'مصر'], defaults: { currency: 'EGP', timezone: 'Africa/Cairo', workDays: SUN_THU } },
  { names: ['saudi arabia', 'السعودية', 'المملكة العربية السعودية'], defaults: { currency: 'SAR', timezone: 'Asia/Riyadh', workDays: SUN_THU } },
  { names: ['united arab emirates', 'uae', 'الإمارات', 'الإمارات العربية المتحدة'], defaults: { currency: 'AED', timezone: 'Asia/Dubai', workDays: MON_FRI } },
  { names: ['kuwait', 'الكويت'], defaults: { currency: 'KWD', timezone: 'Asia/Kuwait', workDays: SUN_THU } },
  { names: ['qatar', 'قطر'], defaults: { currency: 'QAR', timezone: 'Asia/Qatar', workDays: SUN_THU } },
  { names: ['bahrain', 'البحرين'], defaults: { currency: 'BHD', timezone: 'Asia/Bahrain', workDays: SUN_THU } },
  { names: ['oman', 'عمان', 'عُمان'], defaults: { currency: 'OMR', timezone: 'Asia/Muscat', workDays: SUN_THU } },
  { names: ['jordan', 'الأردن'], defaults: { currency: 'JOD', timezone: 'Asia/Amman', workDays: SUN_THU } },
];

const deviceTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const hoursFor = (workDays: WeekDay[], start = '09:00', end = '17:00'): WorkingHours =>
  WEEK_DAYS.reduce((acc, day) => ({ ...acc, [day]: { start, end, isWorking: workDays.includes(day) } }), {} as WorkingHours);

/** Sensible starting values for a country (falls back to the device timezone, USD, Mon–Fri). */
export function defaultBusinessHours(country?: string): BusinessHoursValue {
  const key = String(country || '').trim().toLowerCase();
  const match = COUNTRY_DEFAULTS.find((c) => c.names.includes(key))?.defaults;
  return {
    timezone: match?.timezone || deviceTimezone(),
    currency: match?.currency || 'USD',
    workingHours: hoursFor(match?.workDays || MON_FRI),
  };
}

/** A stored business's values, with defaults filling any gaps. */
export function businessHoursFrom(business: any): BusinessHoursValue {
  const base = defaultBusinessHours(business?.country);
  const stored = business?.workingHours || {};
  const workingHours = WEEK_DAYS.reduce((acc, day) => {
    const d = stored[day];
    acc[day] = d ? { start: d.start || '09:00', end: d.end || '17:00', isWorking: !!d.isWorking } : base.workingHours[day];
    return acc;
  }, {} as WorkingHours);
  return {
    // UTC/USD are the record's built-in defaults, not a choice — suggest better ones until confirmed.
    timezone: business?.settingsConfirmedAt || (business?.timezone && business.timezone !== 'UTC') ? business.timezone : base.timezone,
    currency: business?.settingsConfirmedAt || (business?.currency && business.currency !== 'USD') ? business.currency : base.currency,
    workingHours: business?.settingsConfirmedAt ? workingHours : (business?.workingHours ? workingHours : base.workingHours),
  };
}

/** "HH:MM" problems a user must fix before saving, or null. */
export function validateBusinessHours(value: BusinessHoursValue): string | null {
  const open = WEEK_DAYS.filter((d) => value.workingHours[d].isWorking);
  if (open.length === 0) return 'businessHours.errorNoDays';
  if (open.some((d) => value.workingHours[d].start >= value.workingHours[d].end)) return 'businessHours.errorTimes';
  return null;
}

interface Props {
  value: BusinessHoursValue;
  onChange: (value: BusinessHoursValue) => void;
}

export const BusinessHoursForm: React.FC<Props> = ({ value, onChange }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const days = value.workingHours;
  const openDays = DISPLAY_ORDER.filter((d) => days[d].isWorking);
  // Per-day editing starts open when the stored days already differ.
  const differs = openDays.some((d) => days[d].start !== days[openDays[0]].start || days[d].end !== days[openDays[0]].end);
  const [perDay, setPerDay] = useState(differs);
  const shared = openDays.length ? days[openDays[0]] : { start: '09:00', end: '17:00', isWorking: true };

  const setDay = (day: WeekDay, patch: Partial<WorkingDay>) =>
    onChange({ ...value, workingHours: { ...days, [day]: { ...days[day], ...patch } } });

  const toggleDay = (day: WeekDay) => {
    const turningOn = !days[day].isWorking;
    // A newly opened day takes the shared hours.
    setDay(day, turningOn ? { isWorking: true, start: shared.start, end: shared.end } : { isWorking: false });
  };

  const setShared = (patch: Partial<WorkingDay>) => {
    const next = { ...days };
    for (const d of WEEK_DAYS) if (next[d].isWorking) next[d] = { ...next[d], ...patch };
    onChange({ ...value, workingHours: next });
  };

  return (
    <View style={styles.container}>
      <TimezoneInput
        label={t('businessHours.timezone')}
        value={value.timezone}
        onChange={(tz) => onChange({ ...value, timezone: tz })}
        variant="outlined"
      />
      <CurrencyPicker
        label={t('businessHours.currency')}
        value={value.currency}
        onChange={(code) => onChange({ ...value, currency: code })}
      />

      <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t('businessHours.workingDays')}</Text>
      <View style={[styles.dayRow, { flexDirection: rtl.rowDirection }]}>
        {DISPLAY_ORDER.map((day) => {
          const on = days[day].isWorking;
          return (
            <TouchableOpacity
              key={day}
              style={[styles.dayChip, on && styles.dayChipOn]}
              onPress={() => toggleDay(day)}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: on }}
              accessibilityLabel={t(`businessHours.day.${day}` as any)}
            >
              <Text style={[styles.dayChipText, on && styles.dayChipTextOn]}>{t(`businessHours.dayShort.${day}` as any)}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t('businessHours.workingHours')}</Text>
      {!perDay ? (
        <View style={[styles.timeRow, { flexDirection: rtl.rowDirection }]}>
          <View style={styles.timeCell}>
            <TimePickerField label={t('businessHours.opens')} value={shared.start} onChange={(start) => setShared({ start })} />
          </View>
          <View style={styles.timeCell}>
            <TimePickerField label={t('businessHours.closes')} value={shared.end} onChange={(end) => setShared({ end })} />
          </View>
        </View>
      ) : (
        <View style={styles.perDayList}>
          {DISPLAY_ORDER.filter((d) => days[d].isWorking).map((day) => (
            <View key={day} style={[styles.perDayRow, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.perDayName, { textAlign: rtl.textStart }]}>{t(`businessHours.day.${day}` as any)}</Text>
              <View style={styles.timeCell}>
                <TimePickerField label={t('businessHours.opens')} value={days[day].start} onChange={(start) => setDay(day, { start })} />
              </View>
              <View style={styles.timeCell}>
                <TimePickerField label={t('businessHours.closes')} value={days[day].end} onChange={(end) => setDay(day, { end })} />
              </View>
            </View>
          ))}
        </View>
      )}
      <TouchableOpacity
        onPress={() => {
          // Back to one range: every open day takes the first day's hours.
          if (perDay) setShared({ start: shared.start, end: shared.end });
          setPerDay(!perDay);
        }}
        style={styles.linkButton}
      >
        <Text style={[styles.linkText, { textAlign: rtl.textStart }]}>
          {t(perDay ? 'businessHours.sameHours' : 'businessHours.perDay')}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: { gap: spacing.md },
  label: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.textDark, marginTop: spacing.xs },
  dayRow: { flexWrap: 'wrap', gap: spacing.xs },
  dayChip: {
    minWidth: 44,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    backgroundColor: colors.background.surfaceRaised,
  },
  dayChipOn: { backgroundColor: withAlpha(colors.primary as string, 0.12), borderColor: colors.primary },
  dayChipText: { fontSize: typography.fontSize.sm, color: colors.textLight },
  dayChipTextOn: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  timeRow: { gap: spacing.sm },
  timeCell: { flex: 1, minWidth: 0 },
  perDayList: { gap: spacing.sm },
  perDayRow: { alignItems: 'center', gap: spacing.sm },
  perDayName: { width: 72, fontSize: typography.fontSize.sm, color: colors.textDark },
  linkButton: { paddingVertical: spacing.xs },
  linkText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
});
